import React from "react";
import logo from "../../assets/ic_launcher.png";

const LINKS = {
  docs: "https://vendororbis.featurebase.app/",
  changelog: "https://vendororbis.featurebase.app/changelog",
  discord: "https://discord.gg/UQmQw3bnhs",
  twitter: "https://x.com/vendororbis",
  privacy: "/privacy-policy",
  terms: "/terms-and-condition",
  eula: "/eula",
  email: "apps@vendororbis.com",
};

type QA = { q: string; a: React.ReactNode };

const faqs: QA[] = [
  {
    q: "How do I report a bug or request a feature?",
    a: (
      <>
        Use our{" "}
        <a
          className="text-blue-600 underline"
          href={LINKS.docs}
          target="_blank"
          rel="noreferrer"
        >
          Suggestions board
        </a>{" "}
        or post in{" "}
        <a
          className="text-blue-600 underline"
          href={LINKS.discord}
          target="_blank"
          rel="noreferrer"
        >
          Discord
        </a>
        . For urgent issues, email us at{" "}
        <a className="text-blue-600 underline" href={`mailto:${LINKS.email}`}>
          {LINKS.email}
        </a>
        .
      </>
    ),
  },
  {
    q: "Where can I see what changed recently?",
    a: (
      <>
        Check our{" "}
        <a
          className="text-blue-600 underline"
          href={LINKS.changelog}
          target="_blank"
          rel="noreferrer"
        >
          Changelog
        </a>
        .
      </>
    ),
  },
  {
    q: "Billing and subscriptions help",
    a: (
      <>
        For billing questions, email{" "}
        <a className="text-blue-600 underline" href={`mailto:${LINKS.email}`}>
          {LINKS.email}
        </a>{" "}
        or DM a moderator in Discord. You can also review plans on our{" "}
        <a className="text-blue-600 underline" href="/pricing">
          Pricing
        </a>{" "}
        page.
      </>
    ),
  },
];

const Badge: React.FC<{ label: string; tone?: string }> = ({
  label,
  tone = "blue",
}) => {
  const toneClass =
    tone === "green"
      ? "bg-green-100 text-green-700"
      : tone === "purple"
        ? "bg-purple-100 text-purple-700"
        : tone === "indigo"
          ? "bg-indigo-100 text-indigo-700"
          : tone === "teal"
            ? "bg-teal-100 text-teal-700"
            : tone === "gray"
              ? "bg-gray-100 text-gray-700"
              : "bg-blue-100 text-blue-700";

  return (
    <span
      className={`inline-flex items-center justify-center h-9 w-9 rounded-lg text-xs font-semibold ${toneClass}`}
      aria-hidden="true"
    >
      {label}
    </span>
  );
};

const Card: React.FC<{
  title: string;
  desc: string;
  href?: string;
  onClick?: () => void;
  external?: boolean;
  badge?: React.ReactNode;
}> = ({ title, desc, href, onClick, external, badge }) => {
  const Tag = href ? "a" : "button";
  const commonProps: any = href
    ? {
        href,
        target: external ? "_blank" : undefined,
        rel: external ? "noopener noreferrer" : undefined,
      }
    : { onClick };

  return (
    <Tag
      {...commonProps}
      className="group w-full text-left rounded-2xl bg-white hover:bg-gray-50 transition-colors p-5 border border-gray-200 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
    >
      <div className="flex items-start gap-4">
        <div className="shrink-0 mt-0.5">{badge}</div>
        <div>
          <h3 className="text-gray-900 text-lg font-semibold">{title}</h3>
          <p className="text-gray-600 mt-1">{desc}</p>
        </div>
      </div>
    </Tag>
  );
};

const FAQItem: React.FC<{ item: QA; index: number }> = ({ item, index }) => {
  const [open, setOpen] = React.useState(false);
  const id = React.useMemo(() => `faq-${index}`, [index]);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
      <button
        className="w-full px-4 py-3 flex items-center justify-between bg-white hover:bg-gray-50 text-left"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="text-gray-900 font-medium">{item.q}</span>
        <svg
          className={`h-5 w-5 transition-transform ${open ? "rotate-180" : ""}`}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" />
        </svg>
      </button>
      <div
        id={id}
        className={`px-4 pb-4 text-gray-700 transition-all ${
          open ? "block" : "hidden"
        }`}
      >
        {item.a}
      </div>
    </div>
  );
};

const SupportPage: React.FC = () => {
  return (
    <div className="min-h-[80vh] bg-gray-50 text-gray-900 pt-0">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6 flex items-center gap-4">
          <img src={logo} alt="VendorOrbis" className="h-12 w-12" />
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
              Support
            </h1>
            <p className="text-gray-600">
              Need help? Start with quick links, browse FAQs, or contact us via
              email or Discord.
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-6 space-y-12">
        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Quick links
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card
              title="Suggestions and Feature Requests"
              desc="Share ideas, vote on features, and track progress."
              href={LINKS.docs}
              external
              badge={<Badge label="SG" />}
            />
            <Card
              title="Changelog"
              desc="See what’s new and improved."
              href={LINKS.changelog}
              external
              badge={<Badge label="CL" tone="green" />}
            />
            <Card
              title="Join Discord"
              desc="Chat with the team and community."
              href={LINKS.discord}
              external
              badge={<Badge label="DC" tone="indigo" />}
            />
            <Card
              title="Follow on X"
              desc="Get updates and announcements."
              href={LINKS.twitter}
              external
              badge={<Badge label="X" tone="gray" />}
            />
            <Card
              title="Privacy Policy"
              desc="Learn how we handle your data."
              href={LINKS.privacy}
              badge={<Badge label="PP" tone="teal" />}
            />
            <Card
              title="Terms and EULA"
              desc="Terms of service and license agreement."
              href={LINKS.terms}
              badge={<Badge label="TS" tone="purple" />}
            />
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">FAQs</h2>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <FAQItem key={i} item={f} index={i} />
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Contact us
          </h2>
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
              <h3 className="text-gray-900 font-semibold mb-2">Email support</h3>
              <p className="text-gray-600 mb-4">
                For account or billing questions, email us and we’ll get back to
                you as soon as possible.
              </p>
              <a
                href={`mailto:${LINKS.email}`}
                className="inline-flex items-center justify-center rounded-lg px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white font-medium"
              >
                Email apps@vendororbis.com
              </a>
              <p className="text-xs text-gray-500 mt-3">
                Tip: Include your device, browser, and steps to reproduce.
              </p>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
              <h3 className="text-gray-900 font-semibold mb-2">Community help</h3>
              <p className="text-gray-600 mb-4">
                Join the Discord server to ask questions and get tips from other
                vendors.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href={LINKS.discord}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center rounded-lg px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white font-medium"
                >
                  Join Discord
                </a>
                <a
                  href={LINKS.twitter}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center rounded-lg px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white font-medium"
                >
                  Follow on X
                </a>
              </div>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
              <h3 className="text-gray-900 font-semibold mb-2">Self-service</h3>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>
                  Search the{" "}
                  <a
                    className="text-blue-600 underline"
                    href={LINKS.docs}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Suggestions/Knowledge
                  </a>{" "}
                  board.
                </li>
                <li>
                  Review recent updates in the{" "}
                  <a
                    className="text-blue-600 underline"
                    href={LINKS.changelog}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Changelog
                  </a>
                  .
                </li>
                <li>
                  Follow us on{" "}
                  <a
                    className="text-blue-600 underline"
                    href={LINKS.twitter}
                    target="_blank"
                    rel="noreferrer"
                  >
                    X (Twitter)
                  </a>{" "}
                  for updates.
                </li>
              </ul>
              <div className="mt-4 text-sm text-gray-500">
                Still stuck? Email{" "}
                <a
                  className="text-blue-600 underline"
                  href={`mailto:${LINKS.email}`}
                >
                  {LINKS.email}
                </a>
                .
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default SupportPage;

